import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IFilter } from '../../../common/interfaces/IFilter';

const toDate = new Date();
/*
  We retrieve a list of applications within a specified date range.
  Our goal is to select applications that fall between 12:00 AM on June 15, 2023, and 11:59 PM today.
*/
const endOfDay = new Date(
  toDate.getFullYear(),
  toDate.getMonth(),
  toDate.getDate(),
  23,
  59,
  59,
  999,
);

// Define the state interface
interface FilterState {
  initialized: boolean;
  decisionFilters: IFilter[];
  forReviewFilter: IFilter[];
  stageFilters: IFilter[];
  lifecycleFilters: IFilter[];
  detailedLifecycleFilters: IFilter[];
  userFilters: IFilter[];
  carrierUserFilter: IFilter[];
  productFilters: IFilter[];
  searchFilter: string;
  agencySearchFilter: string;
  fromDate: string;
  toDate: string;
  page: number;
}

// Define payload types for each action
interface InitializationPayload {
  stageFilter: IFilter[];
  lifecycleFilters: IFilter[];
  detailedLifecycleFilters: IFilter[];
  userFilter: IFilter[];
  carrierUserFilter: IFilter[];
  fromDate?: string;
  toDate?: string;
}

interface DateChangePayload {
  fromDate: string;
}

interface ToDateChangePayload {
  toDate: string;
}

interface FilterChangePayload {
  decisionFilter: IFilter[];
}

interface StageFilterPayload {
  stageFilter: IFilter[];
}

interface LifecycleFilterPayload {
  lifecycleFilters: IFilter[];
}

interface DetailedLifecycleFilterPayload {
  detailedLifecycleFilters: IFilter[];
}

interface ForReviewFilterPayload {
  forReviewFilter: IFilter[];
}

interface UserFilterPayload {
  userFilter: IFilter[];
}

interface CarrierUserFilterPayload {
  carrierUserFilter: IFilter[];
}

interface ProductFilterPayload {
  productFilter: IFilter[];
}

interface SearchFilterPayload {
  search: string;
}

interface AgencySearchFilterPayload {
  agencySearch: string;
}

interface PageChangePayload {
  page: number;
}

const initialState: FilterState = {
  initialized: false,
  decisionFilters: [],
  forReviewFilter: [],
  stageFilters: [],
  lifecycleFilters: [],
  detailedLifecycleFilters: [],
  userFilters: [],
  carrierUserFilter: [],
  productFilters: [],
  searchFilter: '',
  agencySearchFilter: '',
  fromDate: new Date(2021, 0, 1, 0, 0, 0, 0).toISOString(),
  toDate: endOfDay.toISOString(),
  page: 1,
};

export const filterSlice = createSlice({
  name: 'applicationStatus',
  initialState,
  reducers: {
    onPrioritizedViewChange: state => {
      // reinitialize the state on prioritized view change
      // this is to ensure the prefilled filters are reapplied
      state.initialized = false;
    },

    onInitializationChange: (
      state,
      action: PayloadAction<InitializationPayload>,
    ) => {
      state.initialized = true;
      state.stageFilters = action.payload.stageFilter;
      state.lifecycleFilters = action.payload.lifecycleFilters;
      state.detailedLifecycleFilters = action.payload.detailedLifecycleFilters;
      state.userFilters = action.payload.userFilter;
      state.carrierUserFilter = action.payload.carrierUserFilter;
      state.fromDate = action.payload.fromDate ?? state.fromDate;
      state.toDate = action.payload.toDate ?? state.toDate;
      state.page = 1;
    },

    onFromDateChange: (state, action: PayloadAction<DateChangePayload>) => {
      state.fromDate = action.payload.fromDate;
      state.page = 1;
    },

    onToDateChange: (state, action: PayloadAction<ToDateChangePayload>) => {
      state.toDate = action.payload.toDate;
      state.page = 1;
    },

    onDecisionFilterChange: (
      state,
      action: PayloadAction<FilterChangePayload>,
    ) => {
      state.decisionFilters = action.payload.decisionFilter;
      state.page = 1;
    },

    onStageFilterChange: (state, action: PayloadAction<StageFilterPayload>) => {
      state.stageFilters = action.payload.stageFilter;
      state.page = 1;
    },

    onLifecycleFilterChange: (
      state,
      action: PayloadAction<LifecycleFilterPayload>,
    ) => {
      state.lifecycleFilters = action.payload.lifecycleFilters;
      state.page = 1;
    },

    onDetailedLifecycleFilterChange: (
      state,
      action: PayloadAction<DetailedLifecycleFilterPayload>,
    ) => {
      state.detailedLifecycleFilters = action.payload.detailedLifecycleFilters;
      state.page = 1;
    },

    onForReviewFilterChange: (
      state,
      action: PayloadAction<ForReviewFilterPayload>,
    ) => {
      state.forReviewFilter = action.payload.forReviewFilter;
      state.page = 1;
    },

    onUserFilterChange: (state, action: PayloadAction<UserFilterPayload>) => {
      state.userFilters = action.payload.userFilter;
      state.page = 1;
    },

    onCarrierUserFilterChange: (
      state,
      action: PayloadAction<CarrierUserFilterPayload>,
    ) => {
      state.carrierUserFilter = action.payload.carrierUserFilter;
      state.page = 1;
    },

    onProductFilterChange: (
      state,
      action: PayloadAction<ProductFilterPayload>,
    ) => {
      state.productFilters = action.payload.productFilter;
      state.page = 1;
    },

    onSearchFilterChange: (
      state,
      action: PayloadAction<SearchFilterPayload>,
    ) => {
      state.searchFilter = action.payload.search;
      state.page = 1;
    },

    onAgencySearchFilterChange: (
      state,
      action: PayloadAction<AgencySearchFilterPayload>,
    ) => {
      state.agencySearchFilter = action.payload.agencySearch;
      state.page = 1;
    },

    onPageChange: (state, action: PayloadAction<PageChangePayload>) => {
      state.page = action.payload.page;
    },
  },
});

export const filterReducer = filterSlice.reducer;
export const filterActions = filterSlice.actions;

export const taskViewSlice = createSlice({
  name: 'taskViewStatus',

  initialState: {
    lastSectionIndex: -1,
    lastFilterIndex: -1,
    page: 0,
  },

  reducers: {
    onTaskViewChange: (state, action) => {
      state.lastSectionIndex = action.payload.lastSectionIndex;
      state.lastFilterIndex = action.payload.lastFilterIndex;
      state.page = 0;
    },

    onPageChange: (state, action) => {
      state.page = action.payload.page;
    },
  },
});

export const taskViewReducer = taskViewSlice.reducer;
export const taskViewActions = taskViewSlice.actions;
